import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  getUser, getSelectedMenu, getSelectedMenuItem, toggleMenu,
  checkPermisson, requiresSaveBeforeNavigation, getSelectedSubMenu,
  toggleSubMenu, getIsTennisEnabled,
} from '../../selectors/common';
import {
  PAGES, MENU, MENU_ITEMS, MENU_ROUTES, PERMISSIONS,
  MODALS, MENU_TYPES, SUBMENU_ITEMS, TENNIS_ROUTE,
} from '../../constants';
import Header from '../../components/Header';
import { getInitials } from '../../utils/common';
import { isModified } from '../../selectors/autobooking';
import MenuItem from './MenuItem';
import { aLogout } from '../../reducers';
import { aSetNavigationInfo, aSetOpenModal, aSetSelectedMenu, aSetSelectedSubMenu } from '../../reducers/common';
import { aSetTempLink } from '../../reducers/autobooking';

const mapToProps = (state) => ({
  user: getUser(state),
  selectedMenu: getSelectedMenu(state),
  selectedSubMenu: getSelectedSubMenu(state),
  selectedMenuItem: getSelectedMenuItem(state),
  toggleMenu: toggleMenu(state),
  toggleSubMenu: toggleSubMenu(state),
  checkPermisson: (payload) => checkPermisson(state, payload),
  isModified: isModified(state),
  requiresSaveBeforeNavigation: requiresSaveBeforeNavigation(state),
  isTennisEnabled: getIsTennisEnabled(state),
});

const actionsToProps = (dispatch) => ({
  setOpenModal: (payload) => dispatch(aSetOpenModal(payload)),
  logout: (payload) => dispatch(aLogout(payload)),
  setSelectedMenu: (payload) => dispatch(aSetSelectedMenu(payload)),
  setSelectedSubMenu: (payload) => dispatch(aSetSelectedSubMenu(payload)),
  setTempLink: (payload) => dispatch(aSetTempLink(payload)),
  setNavigationInfo: (payload) => dispatch(aSetNavigationInfo(payload)),
});

/**
 * @class
 * @property {object} props
 * @property {object} props.user Logged user
 * @property {string} props.selectedMenu Selected root item page
 * @property {string} props.selectedMenuItem Selected item from dropdown
 * @property {boolean} props.toggleMenu Indicate is menu item is changed and
 * dropdowns need to recalculate
 * @property {boolean} props.checkPermisson Show only allowed menu items
 * @property {Function} props.logout Logout user
 * @property {Function} props.setSelectedMenu Change selected root item from sidebar
 */
class Menu extends Component {
  constructor(props) {
    super(props);
    /**
     * @member {object}
     * @property {boolean} isOpened Indicate is dropdown opened
     */
    this.state = {
      isOpened: false,
      isSubMenuOpened: false,
    };
  }

  /**
   * Open dropdown on side nav
   *
   * @returns {void}
   */
  componentDidMount() {
    this.toggleMenu(this.props.selectedMenu, this.props.selectedMenu);
    this.toggleSubMenu(this.props.selectedSubMenu, this.props.selectedSubMenu);
    if (!this.props.user) {
      this.props.navigate(PAGES.LOGIN);
    }
  }

  /**
   * Toggle menu on toggleMenu flag change
   *
   * @param {object} prevProps
   * @returns {void}
   */
  componentDidUpdate(prevProps) {
    if (this.props.toggleMenu !== prevProps.toggleMenu
      && this.props.selectedMenu !== prevProps.selectedMenu) {
      this.toggleMenu(this.props.selectedMenu, prevProps.selectedMenu);
    }
    if (this.props.toggleSubMenu !== prevProps.toggleSubMenu
      && this.props.selectedSubMenu !== prevProps.selectedSubMenu) {
      this.toggleSubMenu(this.props.selectedSubMenu, prevProps.selectedSubMenu);
    }
  }

  /**
   * Return class name for root items
   *
   * @function
   * @param {string} classSection
   * @param {boolean} hasDropdown
   * @returns {string}
   */
  // eslint-disable-next-line sonarjs/no-duplicate-string
  getMenuClass = (classSection, hasDropdown) => `${classSection}__list-item-link ${hasDropdown && 'has-dropdown'}  `;

  /**
   * Return class name for dropdown items
   *
   * @function
   * @param {string} menuItem
   * @param {string} classSection
   * @param {string} classDropdown
   * @param {boolean} hasDropdown
   * @returns {string}
   */
  getMenuItemClass = (menuItem, classSection, classDropdown, hasDropdown = false) => {
    let result = `${classSection}__${classDropdown} ${hasDropdown && 'has-dropdown'} `;
    
    if (menuItem.path === this.props.selectedMenuItem
      || (menuItem.key === this.props.selectedSubMenu && !hasDropdown)) {
      result += 'active ';
    }
    return result;
  };

  /**
   * Return only autificated routes
   *
   * @function
   * @param {object} items
   * @returns {Array}
   */
  getAuthItems = (items) => {
    if (items) {
      const newItems = [];
      Object.keys(items).map((key) => {
        if (this.props.checkPermisson(PERMISSIONS[key])) {
          newItems.push(items[key]);
        }
      });
      return newItems;
    }
    return null;
  };

  /**
   * Logout user
   *
   * @function
   * @returns {void}
   */
  logout = () => {
    this.props.logout({
      callAPI: true,
      location: this.props.location,
      navigate: this.props.navigate,
    });
  };

  /**
   * Toggle dropdowns menu
   *
   * @function
   * @param {string} menuName
   * @param {string} prevMenu
   * @returns {void}
   */
  toggleMenu = (menuName, prevMenu) => {
    let isOpened = !this.state.isOpened;
    if (prevMenu !== menuName) {
      isOpened = true;
      this.props.setSelectedMenu({ menu: menuName });
    }
    this.setState({
      isOpened,
    });
  };

  /**
   * onclick menu
   *
   * @function
   * @param {string} menu
   * @param {boolean} hasDropdown
   * @returns {void}
   */
  onClickMenu = (menu, hasDropdown) => {
    if (!hasDropdown) {
      this.props.setSelectedMenu(menu.key);
      this.handleNavigationClick(menu.path);
    } else {
      this.toggleMenu(
        menu.key, this.props.selectedMenu,
      );
    }
  };

  /**
   * Toggle submenu
   *
   * @function
   * @param {string} subMenuName
   * @param {string} prevSubMenu
   * @returns {void}
   */
  toggleSubMenu = (subMenuName, prevSubMenu) => {
    let isOpened = !this.state.isSubMenuOpened;
    if (subMenuName !== prevSubMenu) {
      isOpened = true;
      this.props.setSelectedSubMenu({ subMenuName });
    }
    this.setState({
      isSubMenuOpened: isOpened,
    });
  };

  /**
   * Onclick SubMenu
   *
   * @function
   * @param {string} subMenu
   * @param {boolean} hasDropdown
   * @returns {void}
   */
  onClickSubMenu = (subMenu, hasDropdown) => {
    if (!hasDropdown) {
      this.props.setSelectedSubMenu({subMenuName: subMenu.key});
      this.handleNavigationClick(subMenu.path);
    } else {
      this.toggleSubMenu(
        subMenu.key, this.props.selectedSubMenu,
      );
    }
  };

  /**
   * Render menu items
   *
   * @function
   * @param {string} classSection
   * @returns {view}
   */
  renderMenu = (classSection) => (
    MENU_ROUTES.map((route) => {
      const isTennisDisabled = route === TENNIS_ROUTE && !this.props.isTennisEnabled;
      if (isTennisDisabled) return null;
      if (this.props.checkPermisson(PERMISSIONS[MENU[route].permission])) {
        const children = this.getAuthItems(MENU_ITEMS[route]);
        const hasDropdown = !MENU[route].path;
        const isSelectedMenu = (MENU[route].path && MENU[route].key === this.props.selectedMenu)
          || (hasDropdown && MENU[route].key === this.props.selectedMenu && this.state.isOpened);
        const shouldDropdown = hasDropdown && children && children.length > 0;
        return (
          <MenuItem
            key={route}
            isSelectedMenu={isSelectedMenu}
            className={`${classSection}__list-item`}
            classMenu={this.getMenuClass(classSection, hasDropdown)}
            onClickMenu={this.onClickMenu.bind(this, MENU[route], hasDropdown)}
            route={route}
            label={MENU[route].name}
          >
            {shouldDropdown && (
              <div className={`${classSection}__dropdown`} id={`${MENU[route].key}_dropdown`}>
                {children.map((item) => {
                  const hasItemDropdown = !item.path;
                  const SubMenuChildren = this.getAuthItems(SUBMENU_ITEMS[item.key]);
                  const isSelectedSubMenu = (item.path && item.key === this.props.selectedSubMenu)
                    || (hasItemDropdown && item.key === this.props.selectedSubMenu
                      && this.state.isSubMenuOpened);
                  const shouldDropdownSubMenu = hasItemDropdown && SubMenuChildren
                    && SubMenuChildren.length > 0;
                  return (
                    <MenuItem
                      key={item.key}
                      isSelectedMenu={isSelectedSubMenu}
                      classMenu={this.getMenuItemClass(item, classSection, 'dropdown-linkTxt', hasItemDropdown)}
                      className={this.getMenuItemClass(item, classSection, 'dropdown-link')}
                      onClickMenu={this.onClickSubMenu.bind(this, item, hasItemDropdown)}
                      route={item.key}
                      label={item.name}
                    >
                      {shouldDropdownSubMenu && (
                        <div className={`${classSection}__dropdown`} id={`${MENU[route].key}_dropdown`}>
                          {SubMenuChildren.map((itemChild) => (
                            <div
                              className={this.getMenuItemClass(itemChild, classSection, 'dropdown-linkList')}
                              key={itemChild.path}
                              onClick={this.handleNavigationClick.bind(this, itemChild.path)}
                            >
                              <div className={this.getMenuItemClass(itemChild, classSection, 'dropdown-linkListItem')}>
                                {itemChild.name}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                    </MenuItem>
                  );
                })}
              </div>
            )}
          </MenuItem>
        );
      }
      return null;
    })
  );

  /**
   * Render home page
   *
   * @function
   * @returns {view}
   */
  renderHomePage = () => (
    <div className="home">
      <div className="home__wrap container">
        <div className="row align-items-center justify-content-center no-gutters">
          <div className="col-md-4">
            <div className="home__panel">
              <div className="home__panel-wrap">
                <div className="logo" />
                <div className="home__panel-title m-t-20">
                  <span>Zoom</span>
                  backoffice
                </div>
                <div className="home__panel-txt m-t-10">backoffice management system</div>
                <div className="home__panel-user m-t-20">{this.props.user && this.props.user.name}</div>
                <Link
                  to={PAGES.MY_PROFILE}
                  className="home__panel-profile m-t-5"
                >
                  {MENU_ITEMS.PROFILE.MY_PROFILE.name}
                </Link>
                <div className="m-t-20 text-center">
                  <button
                    id="logoutBtn_MenuSection"
                    type="button"
                    className="btn btn-primary"
                    onClick={this.logout}
                  >
                    Logout
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-5">
            <div className="home__menu-wrap">
              <div className="home__menu-title">
                Welcome to
                <span> Zoom Backoffice,</span>
              </div>
              <div className="home__menu-txt">Choose your option below.</div>
              <div className="home__menu__list">
                {this.renderMenu(MENU_TYPES.HOME_MENU)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  /**
   * Render side nav
   *
   * @function
   * @returns {view}
   */

  handleNavigationClick = (link) => {
    // this functionality will be refactored in a separate MR, to avoid this one being too big
    // isModified functionality for all pages will be replaced by requiresSaveBeforeNavigation

    if (this.props.isModified || this.props.requiresSaveBeforeNavigation) {
      this.props.setOpenModal({
        modal: MODALS.SAVE_BEFORE_NAVIGATION,
      });
      if (this.props.isModified) {
        this.props.setTempLink({
          tempLink: link,
        });
      } else if (this.props.requiresSaveBeforeNavigation) {
        this.props.setNavigationInfo({ nextNavigationLink: link });
      }
    } else {
      this.props.navigate(link);
    }
  };

  renderSideNav = () => (
    <>
      <div className="side-nav">
        <div className="side-nav__head" />
        <div className="side-nav__user">
          <div className="side-nav__user-logo"><span>{this.props.user && getInitials(this.props.user.name)}</span></div>
          <div className="side-nav__user-name m-t-10">{this.props.user && this.props.user.name}</div>
          <div
            className="side-nav__user-link"
            onClick={this.handleNavigationClick.bind(this, PAGES.MY_PROFILE)}
          >
            {MENU_ITEMS.PROFILE.MY_PROFILE.name}
          </div>
        </div>
        <div className="side-nav__title">Dashboards</div>
        <div className="side-nav__list">
          {this.renderMenu(MENU_TYPES.DASHBOARD_MENU)}
        </div>
      </div>
      <Header
        logout={this.logout}
      />
    </>
  );

  /**
   * Render
   *
   * @returns {view}
   */
  render() {
    return (
      this.props.location.pathname === PAGES.HOME
        ? this.renderHomePage()
        : this.renderSideNav()

    );
  }
}

export default connect(mapToProps, actionsToProps)(Menu);
